// app_client/src/store/useAccountStore.js
import { create } from 'zustand';
import axios from 'axios'; 

const getResponseList = (payload) => {
  const data = payload?.data;
  if (Array.isArray(data)) return data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload)) return payload;
  return [];
};

const getResponseMeta = (payload = {}) => payload.meta || payload;

const mergeAccountSnapshot = (existing = {}, updates = {}) => ({
  ...existing,
  ...updates,
  balances: updates.balances || existing.balances,
  accountSummary: updates.accountSummary || existing.accountSummary,
  currencyOrder: updates.currencyOrder || existing.currencyOrder,
  hasBalanceActivity: updates.hasBalanceActivity ?? existing.hasBalanceActivity
});

const useAccountStore = create((set, get) => ({
  accounts: [],
  currentAccount: null,
  accountBalances: {},
  currentLedger: [],
  currentOperations: [],
  currentOperationsError: '',
  currentOperationDocuments: [],
  notifications: [],
  loading: false,
  lastParams: {},
  currentLedgerReport: null,
  pagination: {
    totalItems: 0,
    currentPage: 1,
    totalPages: 1,
    limit: 10
  },

  clearCurrentAccount: () => {
    set({
      currentAccount: null,
      accountBalances: {},
      currentLedger: [],
      currentLedgerReport: null,
      currentOperations: [],
      currentOperationsError: '',
      currentOperationDocuments: [],
      loading: false
    });
  },

  // Tüm carileri getir
  fetchAccounts: async (params = {}) => {
    const queryParams = {
      page: params.page || 1,
      limit: params.limit || 10,
      ...params
    };

    set({ loading: true, lastParams: queryParams });
    try {
      const response = await axios.get('/api/accounts', { params: queryParams });
      const items = getResponseList(response.data);
      const meta = getResponseMeta(response.data);

      set({ 
        accounts: items,
        pagination: {
          totalItems: meta.total || meta.totalItems || 0,
          currentPage: meta.page || meta.currentPage || 1,
          totalPages: meta.totalPages || 1,
          limit: queryParams.limit || 10
        },
        loading: false
      });
    } catch (err) {
      set({ loading: false });
      console.error("Hesaplar yüklenemedi:", err);
    }
  },

  // Tekil cari getir (Detay sayfası için)
  fetchAccountById: async (id) => {
    if (!id || id === 'new' || id === 'undefined' || id === 'null') {
      return null;
    }

    try {
      const res = await axios.get(`/api/accounts/${id}`);
      const account = res.data.data || res.data;
      const balances = account.balances || {
        TRY: {
          debit: Number(account.debit || 0),
          credit: Number(account.credit || 0),
          balance: Number(account.balance || 0)
        }
      };

      // Mevcut listede varsa güncelle, yoksa ekle (opsiyonel ama sağlıklı)
      set((state) => ({
        accounts: Array.isArray(state.accounts) && state.accounts.find(a => a._id === id)
          ? state.accounts.map(a => a._id === id ? account : a)
          : [...(Array.isArray(state.accounts) ? state.accounts : []), account],
        currentAccount: account,
        accountBalances: {
          ...state.accountBalances,
          [id]: balances
        }
      }));
      return { ...account, balances };
    } catch (err) {
      console.error("Hesap detayı çekilemedi:", err);
      return null;
    }
  },

  downloadAccountLedgerPDF: async (accountId, lang = 'tr', filters = {}) => {
    try {
      const requestedLang = String(lang || 'tr').trim().toLowerCase();
      const safeLang = ['tr', 'en', 'de'].includes(requestedLang) ? requestedLang : 'tr';
      const params = new URLSearchParams({ lang: safeLang });
      if (filters.startDate) params.set('startDate', filters.startDate);
      if (filters.endDate) params.set('endDate', filters.endDate);
      if (filters.reportCurrency) params.set('reportCurrency', String(filters.reportCurrency).trim().toUpperCase());

      const response = await axios.get(`/api/accounts/${accountId}/ledger/pdf?${params.toString()}`, {
        responseType: 'blob'
      });

      const contentDisposition = response.headers?.['content-disposition'] || '';
      const encodedFileName = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
      const quotedFileName = contentDisposition.match(/filename="([^"]+)"/i)?.[1];
      const downloadFileName = encodedFileName
        ? decodeURIComponent(encodedFileName)
        : quotedFileName || `ledger_${safeLang}.pdf`;

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadFileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (err) {
      console.error("PDF ekstre indirilemedi:", err);
      return {
        success: false,
        message: err.response?.data?.message || "PDF ekstre indirilemedi."
      };
    }
  },

  downloadAccountStatementPDF: async (accountId, lang = 'tr', filters = {}) => {
    const extractErrorMessage = async (error) => {
      const fallbackMessage = error?.response?.data?.message || 'Hesap ekstresi PDF indirilemedi.';
      const payload = error?.response?.data;

      if (!payload || typeof payload === 'string') {
        return fallbackMessage;
      }

      if (payload instanceof Blob) {
        try {
          const text = await payload.text();
          const parsed = JSON.parse(text);
          return parsed?.message || parsed?.error || fallbackMessage;
        } catch {
          return fallbackMessage;
        }
      }

      return fallbackMessage;
    };

    try {
      const requestedLang = String(lang || 'tr').trim().toLowerCase();
      const safeLang = ['tr', 'en', 'de'].includes(requestedLang) ? requestedLang : 'tr';
      const params = new URLSearchParams({ lang: safeLang });
      if (filters.startDate) params.set('startDate', filters.startDate);
      if (filters.endDate) params.set('endDate', filters.endDate);

      const response = await axios.get(`/api/transactions/account/${accountId}/statement/pdf?${params.toString()}`, {
        responseType: 'blob'
      });

      const contentDisposition = response.headers?.['content-disposition'] || '';
      const encodedFileName = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
      const quotedFileName = contentDisposition.match(/filename="([^"]+)"/i)?.[1];
      const downloadFileName = encodedFileName
        ? decodeURIComponent(encodedFileName)
        : quotedFileName || `statement_${safeLang}.pdf`;

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadFileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (err) {
      console.error("Hesap ekstresi PDF indirilemedi:", err);
      const message = await extractErrorMessage(err);
      return {
        success: false,
        message
      };
    }
  },

  // Kaydet veya Güncelle (Tek bir akış üzerinden)
  saveAccount: async (formData, id = null) => {
    try {
      const isUpdate = !!id;
      const url = isUpdate ? `/api/accounts/${id}` : '/api/accounts';
      const method = isUpdate ? 'put' : 'post';
      
      const res = await axios[method](url, formData);
      const savedData = res.data.data || res.data;

      set((state) => ({
        accounts: isUpdate
          ? (Array.isArray(state.accounts) ? state.accounts : []).map((acc) => (
              acc._id === id ? mergeAccountSnapshot(acc, savedData) : acc
            ))
          : [...(Array.isArray(state.accounts) ? state.accounts : []), savedData],
        currentAccount: isUpdate
          ? mergeAccountSnapshot(
              (Array.isArray(state.accounts) ? state.accounts.find((acc) => acc._id === id) : null) || state.currentAccount || {},
              savedData
            )
          : savedData
      }));

      return { success: true, data: savedData };
    } catch (err) {
      return { 
        success: false, 
        message: err.response?.data?.message || "Sunucuyla iletişim kurulamadı." 
      };
    }
  },

  // Yardımcı: Sıradaki kodu getir
  getNextCode: async (type = 'CUSTOMER') => {
    try {
      const res = await axios.get(`/api/accounts/next-code?type=${type}`);
      return res.data.data; 
    } catch (err) {
      console.error("Sıradaki kod alınamadı:", err);
      return 'CARI-???';
    }
  },

  // Yardımcı: Kod kullanımda mı?
  checkCodeUsage: async (code, excludeId = '') => {
    try {
      // excludeId boşsa query'ye eklemiyoruz
      const url = `/api/accounts/check-code?code=${code}${excludeId ? `&exclude=${excludeId}` : ''}`;
      const res = await axios.get(url);
      return res.data.exists; 
    } catch (err) {
      console.error("Kod kontrolü başarısız:", err);
      return false;
    }
  },

  // Cari Sil
  deleteAccount: async (id) => {
    try {
      await axios.delete(`/api/accounts/${id}`);
      const { accounts, pagination, lastParams } = get();
      const nextPage = accounts.length === 1 && pagination.currentPage > 1
        ? pagination.currentPage - 1
        : pagination.currentPage;

      await get().fetchAccounts({ ...lastParams, page: nextPage });
      return { success: true };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.message || err.response?.data?.error || "Silme işlemi başarısız."
      };
    }
  },

  toggleWatch: async (id) => {
    try {
      const res = await axios.post(`/api/accounts/${id}/watch`);
      const { isWatching, data: updatedAccount } = res.data;
      
      set((state) => ({
        accounts: (Array.isArray(state.accounts) ? state.accounts : []).map((acc) => (
          acc._id === id
            ? { ...mergeAccountSnapshot(acc, updatedAccount || {}), priority: isWatching ? 2 : 1 }
            : acc
        )),
        currentAccount: state.currentAccount?._id === id
          ? { ...mergeAccountSnapshot(state.currentAccount, updatedAccount || {}), priority: isWatching ? 2 : 1 }
          : state.currentAccount
      }));
      return { success: true, isWatching, data: updatedAccount };
    } catch (err) {
      console.error("İzleme durumu değiştirilemedi:", err);
      return {
        success: false,
        message: err.response?.data?.message || "İzleme durumu değiştirilemedi."
      };
    }
  },

  assignAccountResponsible: async (accountId, newAssignedToId) => {
    try {
      const res = await axios.patch(`/api/accounts/${accountId}/assign`, { assignedTo: newAssignedToId });
      const updatedAccount = res.data.data || res.data;
    
      set((state) => ({
        accounts: (Array.isArray(state.accounts) ? state.accounts : []).map(acc => (
          acc._id === accountId ? mergeAccountSnapshot(acc, updatedAccount) : acc
        )),
        currentAccount: state.currentAccount?._id === accountId
          ? mergeAccountSnapshot(state.currentAccount, updatedAccount)
          : state.currentAccount
      }));
      return { success: true, data: updatedAccount };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || "Sorumlu değiştirilemedi." };
    }
  },

  fetchAccountLedger: async (id, filters = {}) => {
    set({ loading: true });
    try {
      const res = await axios.get(`/api/accounts/${id}/ledger`, { params: filters });
      const report = res.data.data || res.data || {};
      const activeReport = report.reportingCurrencyReport || report;
      const ledgerRows = Array.isArray(activeReport.sections)
        ? activeReport.sections.flatMap((section) => section.rows || [])
        : [];

      set({
        currentLedger: ledgerRows,
        currentLedgerReport: report,
        loading: false
      });
      return report;
    } catch (err) {
      set({ currentLedger: [], currentLedgerReport: null, loading: false });
      console.error("Cari ekstre yüklenemedi:", err);
      return null;
    }
  },

  fetchAccountOperations: async (accountId) => {
    set({ loading: true, currentOperationsError: '' });
    try {
      const res = await axios.get(`/api/operations/account/${accountId}`);
      const operations = res.data.data || res.data || [];
      set({ currentOperations: operations, currentOperationsError: '', loading: false });
      return operations;
    } catch (err) {
      const message = err.response?.data?.message || 'Operasyonlar yüklenemedi.';
      set({ currentOperations: [], currentOperationsError: message, loading: false });
      console.error("Cari operasyonları yüklenemedi:", err);
      return [];
    }
  },

  createOperation: async (operationData) => {
    try {
      const res = await axios.post('/api/operations', operationData);
      if (operationData.account) {
        await get().fetchAccountOperations(operationData.account);
      }
      return { success: true, data: res.data.data || res.data };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.message || err.response?.data?.error || "Operasyon oluşturulamadı."
      };
    }
  },

  fetchOperationDocuments: async (operationId) => {
    set({ loading: true });
    try {
      const res = await axios.get(`/api/documents/operation/${operationId}`);
      const documents = res.data.data || res.data || [];
      set({ currentOperationDocuments: documents, loading: false });
      return documents;
    } catch (err) {
      set({ currentOperationDocuments: [], loading: false });
      console.error("Operasyon evrakları yüklenemedi:", err);
      return [];
    }
  },

  uploadOperationDocument: async (operationId, formData) => {
    try {
      const res = await axios.post(`/api/documents/operations/${operationId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      await get().fetchOperationDocuments(operationId);
      return { success: true, data: res.data.data || res.data };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.message || err.response?.data?.error || "Evrak yüklenemedi."
      };
    }
  },

  fetchMyNotifications: async () => {
    try {
      const res = await axios.get('/api/notifications/my', {
        params: { unreadOnly: true, limit: 5 }
      });
      const notifications = res.data.data || res.data || [];
      set({ notifications });
      return notifications;
    } catch (err) {
      set({ notifications: [] });
      console.error("Bildirimler yüklenemedi:", err);
      return [];
    }
  },

  fetchNotifications: async (params = {}) => {
    try {
      const res = await axios.get('/api/notifications/my', { params });
      return {
        success: true,
        data: res.data.data || res.data || []
      };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.message || "Bildirimler yüklenemedi."
      };
    }
  },

  markNotificationAsRead: async (id, isRead = true) => {
    try {
      await axios.put(`/api/notifications/${id}/read`, { isRead });
      set((state) => ({
        notifications: state.notifications.filter((item) => item._id !== id)
      }));
      return { success: true };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.message || "Bildirim güncellenemedi."
      };
    }
  },

  toggleNotificationPin: async (id, isPinned) => {
    try {
      const res = await axios.put(`/api/notifications/${id}/pin`, { isPinned });
      return {
        success: true,
        data: res.data.data
      };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.message || "Bildirim pin durumu güncellenemedi."
      };
    }
  }
}));

export default useAccountStore;
